import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { AppIconComponent } from './app-icon.component';

export type AppButtonVariant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'ghost'
  | 'outline';
export type AppButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type AppButtonType = 'button' | 'submit' | 'reset';

/**
 * AppButton - Componente de botão padronizado para o design system
 *
 * @example
 * ```html
 * <app-button
 *   variant="primary"
 *   size="md"
 *   [loading]="isLoading"
 *   (clicked)="handleAction()">
 *   Salvar
 * </app-button>
 * ```
 */
@Component({
  selector: 'app-button',
  standalone: true,
  imports: [NgClass, AppIconComponent],
  template: `
    <button
      [type]="type"
      [disabled]="isDisabled || isLoading"
      [ngClass]="computedClasses()"
      (click)="handleClick($event)"
      [attr.aria-busy]="isLoading"
      [attr.aria-label]="ariaLabel"
    >
      @if (isLoading) {
      <div
        class="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"
        aria-label="Carregando"
      ></div>
      } @else if (iconLeft) {
      <app-icon
        [name]="iconLeft"
        size="sm"
        class="icon-left"
        aria-hidden="true"
      >
      </app-icon>
      }

      <span class="button-content">
        <ng-content></ng-content>
      </span>

      @if (!isLoading && iconRight) {
      <app-icon
        [name]="iconRight"
        size="sm"
        class="icon-right"
        aria-hidden="true"
      >
      </app-icon>
      }
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        font-weight: 500;
        border-radius: 0.5rem;
        transition: all 0.2s ease;
        outline: none;
        border: none;
        cursor: pointer;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      button:focus {
        outline: 2px solid transparent;
        outline-offset: 2px;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
      }

      button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }

      /* Variants */
      .variant-primary {
        background-color: #3b82f6;
        color: white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .variant-primary:hover:not(:disabled) {
        background-color: #2563eb;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .variant-secondary {
        background-color: #6b7280;
        color: white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .variant-secondary:hover:not(:disabled) {
        background-color: #4b5563;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .variant-accent {
        background-color: #8b5cf6;
        color: white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .variant-accent:hover:not(:disabled) {
        background-color: #7c3aed;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .variant-ghost {
        background-color: transparent;
        color: #374151;
      }

      .variant-ghost:hover:not(:disabled) {
        background-color: #f3f4f6;
        color: #111827;
      }

      .variant-outline {
        background-color: transparent;
        border: 2px solid #d1d5db;
        color: #374151;
      }

      .variant-outline:hover:not(:disabled) {
        border-color: #3b82f6;
        color: #2563eb;
      }

      /* Sizes */
      .size-sm {
        padding: 0.375rem 0.75rem;
        font-size: 0.75rem;
        line-height: 1rem;
      }

      .size-md {
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .size-lg {
        padding: 0.75rem 1.5rem;
        font-size: 1rem;
        line-height: 1.5rem;
      }

      .size-xl {
        padding: 1rem 2rem;
        font-size: 1.125rem;
        line-height: 1.75rem;
      }

      .full-width {
        width: 100%;
      }

      /* Icon spacing */
      .icon-left {
        margin-right: 0.25rem;
      }

      .icon-right {
        margin-left: 0.25rem;
      }

      .button-content {
        display: flex;
        align-items: center;
        font-weight: inherit;
      }

      /* Loading animation */
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .animate-spin {
        animation: spin 1s linear infinite;
      }
    `,
  ],
})
export class AppButtonComponent {
  // Enhanced inputs com signals
  private _variant = signal<AppButtonVariant>('primary');
  private _size = signal<AppButtonSize>('md');
  private _disabled = signal(false);
  private _loading = signal(false);
  private _fullWidth = signal(false);

  @Input() type: AppButtonType = 'button';
  @Input() iconLeft?: string;
  @Input() iconRight?: string;
  @Input() ariaLabel?: string;

  // Enhanced inputs (Angular 20)
  @Input({ required: false }) set buttonVariant(value: AppButtonVariant) {
    this._variant.set(value);
  }

  @Input({ required: false }) set variant(value: AppButtonVariant) {
    this._variant.set(value);
  }

  @Input({ required: false }) set disabled(value: boolean) {
    this._disabled.set(value);
  }

  @Input({ required: false }) set buttonSize(value: AppButtonSize) {
    this._size.set(value);
  }

  @Input({ required: false }) set isDisabled(value: boolean) {
    this._disabled.set(value);
  }

  @Input({ required: false }) set isLoading(value: boolean) {
    this._loading.set(value);
  }

  @Input({ required: false }) set isFullWidth(value: boolean) {
    this._fullWidth.set(value);
  }

  // Getters for computed access
  get variant() {
    return this._variant();
  }
  size = computed(() => this._size());
  get disabled() {
    return this._disabled();
  }
  loading = computed(() => this._loading());
  fullWidth = computed(() => this._fullWidth());

  // Template-friendly getters
  get isDisabled() {
    return this._disabled();
  }
  get isLoading() {
    return this._loading();
  }
  get isFullWidth() {
    return this._fullWidth();
  }

  // Enhanced output com tipo específico
  @Output() clicked = new EventEmitter<MouseEvent>();

  // Computed classes usando signals
  computedClasses = computed(() => {
    return [
      `variant-${this.variant}`,
      `size-${this.size()}`,
      this.fullWidth() ? 'full-width' : '',
    ]
      .filter(Boolean)
      .join(' ');
  });

  handleClick(event: MouseEvent): void {
    if (!this.isDisabled && !this.isLoading) {
      this.clicked.emit(event);
    }
  }
}
